import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'

import { GasParams, Token } from 'types/entities'

import { useGasFetcher } from 'hooks/api'
import { useAirdrop, useAirdropTransactions, useAirdropWithSignature } from 'hooks/airdrop'

import {
  useGasParams,
  useSignature,
  useAirdropParams,
  useAirdropStatus,
  useIsSelfGenerated,
  useSetAirdropStatus,
  useTransactionsCount,
  useClearPrepareAirdropData,
} from 'state/airdrop/hooks'

import { Links } from 'constants/link'
import { errors } from 'constants/errors'
import { TxTypes, AirdropStatus } from 'constants/transaction'

import { Button } from 'components/ui'
import { convertStringObjToBigInt } from 'utils/helpers'
import { parseErrorToUserReadableMessage } from 'utils/errors'

import { NotificationTypes } from 'notifications/service'
import { useCallNotification } from 'notifications/hooks'

import { AirdropState } from './AirdropState'
import { SendingState, StepName, StepStatus } from './types'

import { ButtonsWrapMultisend } from './Style'

type Props = {
  error: string
  isLoading: boolean
  airdropId: string
  onCurrentClose?: VoidFunction
  sendingState: SendingState | null
  onErrorCallback: (error: string) => void
  setError: Dispatch<SetStateAction<string>>
  setSendingState: (sendingState: SendingState) => void
}

export function Airdrop({
  error,
  setError,
  isLoading,
  airdropId,
  sendingState,
  onCurrentClose,
  setSendingState,
  onErrorCallback,
}: Props) {
  const signature = useSignature()
  const isSelfGenerated = useIsSelfGenerated()
  const transactionsCount = useTransactionsCount()

  const createAirdrop = useAirdrop()
  const createAirdropWithSignature = useAirdropWithSignature()

  const gasParams = useGasParams()
  const airdropParams = useAirdropParams()
  const airdropTransactions = useAirdropTransactions()

  const gasFetcher = useGasFetcher()
  const setAirdropStatus = useSetAirdropStatus()
  const airdropStatus = useAirdropStatus()
  const { transactions } = useAirdropTransactions()

  const navigate = useNavigate()
  const callNotification = useCallNotification()
  const clearPrepareAirdropData = useClearPrepareAirdropData()

  useEffect(() => {
    onCreateAirdrop()
  }, [])

  const backToMainPage = () => {
    navigate(Links.TOKEN)
    clearPrepareAirdropData()
    onCurrentClose?.()
  }

  const clearLocalState = () => {
    setError('')
  }

  const onSendAirdrop = async (token: Token, gasParams: GasParams) => {
    const gasParamsForAirdrop = gasParams || gasFetcher.gasParams

    setSendingState({ name: StepName.SEND, status: StepStatus.PENDING })

    await createAirdrop({ ...airdropParams, airdropId, token }, gasParamsForAirdrop)
  }

  const onSendAirdropWithSignature = async (token: Token, gasParams: GasParams) => {
    if (!signature) {
      throw new Error(errors.validation.SIGN_IS_REQUIRED)
    }
    setSendingState({ name: StepName.SEND, status: StepStatus.PENDING })

    await createAirdropWithSignature({
      ...airdropParams,
      token,
      gasParams,
      airdropId,
      signature: signature.sign,
      expiration: signature.expiration,
    })
  }

  const onCreateAirdrop = async () => {
    try {
      clearLocalState()

      if (!airdropParams.token) {
        throw new Error(errors.validation.TOKEN_IS_REQUIRED)
      }

      if (!gasParams) {
        throw new Error(errors.validation.GAS_PARAMS_ARE_REQUIRED)
      }

      const _gasParams = convertStringObjToBigInt(gasParams) as GasParams

      if (isSelfGenerated) {
        await onSendAirdropWithSignature(airdropParams.token, _gasParams)
      } else {
        await onSendAirdrop(airdropParams.token, _gasParams)
      }

      setSendingState({ name: StepName.SEND, status: StepStatus.SUCCESS })
    } catch (err) {
      setError(parseErrorToUserReadableMessage(err))
      onErrorCallback(parseErrorToUserReadableMessage(err))

      setSendingState({ name: StepName.SEND, status: StepStatus.FAILED })
      callNotification({ type: NotificationTypes.ERROR, message: err.message })
    }
  }

  const onRetry = () => {
    setAirdropStatus(AirdropStatus.NOT_STARTED)

    onCreateAirdrop()
  }

  const isDisabled = useMemo(
    () => Boolean(isLoading || error || !airdropTransactions.isMined),
    [isLoading, error, airdropTransactions.isMined],
  )
  const isSendingLoading = useMemo(() => sendingState?.status === StepStatus.PENDING, [sendingState?.status])

  const actualTxType = useMemo(() => {
    return isSelfGenerated ? TxTypes.AIRDROP_WITH_SIGNATURE : TxTypes.AIRDROP
  }, [isSelfGenerated])

  const airdropType = useMemo(
    () => actualTxType === TxTypes.AIRDROP || actualTxType === TxTypes.AIRDROP_WITH_SIGNATURE,
    [actualTxType],
  )

  const isAirdropStarted = useMemo(() => {
    return transactions.length > 0
  }, [transactions])

  return (
    <>
      <AirdropState
        error={error}
        txType={actualTxType}
        txStatus={airdropStatus}
        totalTxs={transactionsCount}
        onCurrentClose={onCurrentClose}
        txHashes={airdropTransactions.transactions}
      />

      {error && airdropType && (
        <ButtonsWrapMultisend>
          <Button fullSize size='large' onClick={backToMainPage}>
            _start-new-sending
          </Button>

          {!isAirdropStarted && (
            <Button type='primary' fullSize size='large' onClick={onRetry}>
              _retry
            </Button>
          )}
        </ButtonsWrapMultisend>
      )}

      {airdropTransactions.isMined && !error && (
        <Button fullSize size='large' onClick={backToMainPage} disabled={isDisabled} isLoading={isSendingLoading}>
          _start-new-sending
        </Button>
      )}
    </>
  )
}
